import React from "react";
import { Icon } from "@iconify/react";
import { Chip } from "@heroui/react";
import { TRequestActivity } from "@shared/entities/requestActivity";
import { useTranslation } from "react-i18next";

type RequestStatus =
	| "draft"
	| "pending"
	| "change_required"
	| "pending_doctor"
	| "approved_by_doctor"
	| "rejected_by_doctor"
	| "pending_external_documents"
	| "completed"
	| "rejected";

interface ActivityTimelineProps {
	activity: TRequestActivity[];
}

export const ActivityTimeline: React.FC<ActivityTimelineProps> = ({ activity }) => {
	const formatDate = (timestamp: number) => {
		return new Date(timestamp).toLocaleDateString("he-IL", {
			year: "numeric",
			month: "long",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const getActivityIcon = (activity: TRequestActivity): string => {
		if (activity.action.includes("status")) {
			if (activity.action === "ADMIN_COMPLETE") return "lucide:check-circle";
			if (activity.action === "DOCTOR_REJECT") return "lucide:x-circle";
			if (activity.action === "ADMIN_REJECT") return "lucide:x-circle";
			if (activity.action === "ADMIN_REQUEST_CHANGES_FROM_DOCTOR") return "lucide:stethoscope";
			if (activity.action === "DOCTOR_APPROVE") return "lucide:thumbs-up";
			if (activity.action === "ADMIN_ATTACH_DOCTOR") return "lucide:thumbs-down";
			if (activity.action === "ADMIN_REQUEST_CHANGES_FROM_CLIENT")
				return "lucide:alert-triangle";
			return "lucide:refresh-cw";
		}

		if (activity.action.includes("message")) return "lucide:message-square";
		if (activity.action.includes("document")) return "lucide:file";
		if (activity.action.includes("payment")) return "lucide:credit-card";

		return "lucide:activity";
	};

	const getActivityColor = (activity: TRequestActivity): string => {
		if (activity.action === "ADMIN_COMPLETE") return "text-success";
		if (activity.action === "DOCTOR_REJECT") return "text-danger";
		if (activity.action === "DOCTOR_APPROVE") return "text-success";
		if (activity.action === "ADMIN_REQUEST_CHANGES_FROM_CLIENT") return "text-warning";
		if (activity.action === "ADMIN_REQUEST_CHANGES_FROM_DOCTOR") return "text-secondary";
		return "text-default";
	};

	const getActorBadge = (role: string) => {
		switch (role) {
			case "admin":
				return (
					<Chip
						size="sm"
						color="warning"
						variant="flat"
						startContent={<Icon icon="lucide:shield" className="h-3 w-3" />}
					>
						מנהל
					</Chip>
				);
			case "doctor":
				return (
					<Chip
						size="sm"
						color="secondary"
						variant="flat"
						startContent={<Icon icon="lucide:stethoscope" className="h-3 w-3" />}
					>
						רופא
					</Chip>
				);
			case "client":
				return (
					<Chip
						size="sm"
						color="primary"
						variant="flat"
						startContent={<Icon icon="lucide:user" className="h-3 w-3" />}
					>
						מטופל
					</Chip>
				);
			case "system":
				return (
					<Chip
						size="sm"
						color="default"
						variant="flat"
						startContent={<Icon icon="lucide:cpu" className="h-3 w-3" />}
					>
						מערכת
					</Chip>
				);
			default:
				return null;
		}
	};

	const sortedActivities = [...activity].sort((a, b) => b.date - a.date);

	const { t } = useTranslation(["common"]);

	return (
		<div className="relative text-start">
			{/* Timeline line */}
			<div className="absolute top-0 bottom-0 right-4 w-px bg-default-200"></div>

			<div className="space-y-4">
				{sortedActivities.map((activityItem) => (
					<div key={activityItem.id} className="relative pr-10">
						{/* Timeline dot with actor color */}
						<div
							className={`absolute right-0 top-0 w-8 h-8 rounded-full flex items-center justify-center bg-content1 border-2 ${
								activityItem.actor.type === "admin"
									? "border-warning"
									: activityItem.actor.type === "doctor"
									? "border-secondary"
									: activityItem.actor.type === "client"
									? "border-primary"
									: "border-default"
							}`}
						>
							<Icon
								icon={getActivityIcon(activityItem)}
								className={`h-4 w-4 ${getActivityColor(activityItem)}`}
							/>
						</div>

						<div className="bg-content2 p-3 rounded-medium">
							<div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
								<div className="flex flex-col gap-1">
									<div className="flex flex-wrap items-center gap-2">
										{getActorBadge(activityItem.actor.type)}
										<h4 className="font-medium">
											{t(`activityTypes.${activityItem.action}`)}
										</h4>
									</div>
									<div className="flex items-center gap-1 text-sm text-default-500">
										<span>{activityItem.actor.name}</span>
									</div>
								</div>
								<span className="text-xs text-default-400">
									{formatDate(activityItem.date)}
								</span>
							</div>

							{activityItem.message && (
								<p className="text-default-600 text-sm mt-2 p-2 bg-content1 rounded-small">
									{activityItem.message}
								</p>
							)}
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

export const getStatusLabel = (status: RequestStatus): string => {
	const statusLabels: Record<RequestStatus, string> = {
		draft: "טיוטה",
		pending: "ממתין לאישור",
		change_required: "נדרש עדכון",
		pending_doctor: "ממתין לרופא",
		approved_by_doctor: "אושר ע״י רופא",
		rejected_by_doctor: "נדחה ע״י רופא",
		pending_external_documents: "ממתין למסמכים חיצוניים",
		completed: "הושלם",
		rejected: "נדחה",
	};

	return statusLabels[status] || status;
};
